import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ServiceSubscriptionBox, Modal, dateFormatter, currencyFormatter, usePathFactory, useTranslator } from "@sia/style-guide";
import UserContentNavigation from "../UserContentNavigation";
import Page from "pages/Page";
import GlobalContext from "contexts/GlobalContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import { useHistory } from "react-router-dom";
import "./ServicesPage.scss";
import NotFoundPage from "pages/Error/NotFoundPage";
import erpAdapter from "common/Adapters/ERPAdapter";
import KeycloakIDPAdapter from "common/Adapters/IDPAdapter/KeycloakIDPAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import {
  extractBmOutputExtraPropsFromService,
  handleBmOutputProductCancelationDateSelection,
  handleBmOutputProductSelection,
  subscribeToBmOutputService,
  unsubscribeFromBmOutputService,
} from "./bmOutputService";

const ServicesPage = ({ setIsSubscribedToJobs }) => {
  const [services, setServices] = useState([]);
  const [modalState, setModalState] = useState({
    visible: false,
    modalTitle: "",
    modalLead: "",
  });
  const [shouldFetch, setShouldFetch] = useState(true);
  const [handleModalConfirm, setHandleModalConfirm] = useState(() => () => null);
  const { language, isSubscribedToAnyService, user, setIsSubscribedToValueApp } = useContext(GlobalContext);
  const translate = useTranslator();
  const history = useHistory();
  const pathFactory = usePathFactory();
  const [bmUpdate, setBmUpdate] = useState(false);
  const [bmOutputUpdate, setBmOutputUpdate] = useState(false);
  const [resetBmDropdown, setResetBmDropdown] = useState(false);
  const [resetBmOutputDropdown, setResetBmOutputDropdown] = useState(false);

  const [newsletters, setNewsletters] = useState([]);
  const [isSubscribedToNewsLetter, setIsSubscribedToNewsletter] = useState(false);

  const [magazines, setMagazines] = useState([]);
  const [selectedMagazine, setSelectedMagazine] = useState(undefined);
  const [isSubscribedToMagazines, setIsSubscribedToMagazines] = useState(false);
  const [ePaper, setEpaper] = useState(false);
  const [printed, setPrinted] = useState(false);
  const [isMagazineSaveButtonActive, setIsMagazineSaveButtonActive] = useState(true);
  const [selectedDropdownItem, setSelectedDropdownItem] = useState({});
  const [selectedDropdownItems, setSelectedDropdownItems] = useState({});
  const [selectedBmoutputDropdownItems, setSelectedBmoutputDropdownItems] = useState({});
  const [selectedValueAppProduct, setSelectedValueAppProduct] = useState({});
  const [valueAppHelperText, setValueAppHelperText] = useState("");
  const [valueAppPrice, setValueAppPrice] = useState("");

  let noneOptions = useMemo(() => ["None", "Keine", "Aucune", "Nessuna"], []);

  const checkBoxes = useMemo(
    () => ({
      [translate("subscriptionBox.form.e-paper")]: {
        value: ePaper,
        onCheckboxChange: (e, value) => {
          if (!selectedMagazine) {
            setEpaper(false);
            setPrinted(false);
            return;
          }
          setEpaper(value);
        },
      },

      [[translate("subscriptionBox.form.printed")]]: {
        value: printed,
        onCheckboxChange: (e, value) => {
          if (!selectedMagazine) {
            setEpaper(false);
            setPrinted(false);
            return;
          }
          setPrinted(value);
        },
      },
    }),
    [ePaper, printed, selectedMagazine, translate]
  );

  const onMagazineChange = useCallback((value) => {
    setSelectedMagazine(value);
    setServices((serviceState) => {
      let serviceStateCopy = [...serviceState];
      let firstService = serviceState[0];
      firstService.options.selectedValue = value;
      return serviceStateCopy;
    });
  }, []);

  useEffect(() => {
    if (!selectedMagazine) {
      Object.values(checkBoxes).forEach((checkBox) => {
        checkBox.onCheckboxChange(null, false);
      });
      return;
    }
  }, [selectedMagazine, checkBoxes]);

  const REFERENCE_GALLERY_SERVICE_ID = 119055;
  const JOBS_SERVICE_ID = 49899;
  const LAW_SERVICE_ID = 49891;
  const MAGAZINE_SERVICE_ID = 4;
  const NEWSLETTER_SERVICE_ID = 49925;
  const BM_INPUT_ID = 121033;
  const VALUEAPP_INPUT_ID = 121036;
  const BM_OUTPUT_ID = 49890;

  const unsubscribeFromService = (service_id, product_id) => {
    switch (service_id) {
      case JOBS_SERVICE_ID:
        erpAdapter.fetch(
          "POST",
          "account/corporate/services/service/unsubscribe",
          {
            product_id,
          },
          (response) => {
            setShouldFetch(true);
          }
        );
        break;
      // TODO chanage to the correct endpoint when the API is ready.
      case LAW_SERVICE_ID:
        erpAdapter.fetch(
          "POST",
          "account/corporate/services/service/unsubscribe",
          {
            product_id,
          },
          (response) => {
            setShouldFetch(true);
          }
        );
        break;

      // TODO chanage to the correct endpoint when the API is ready.
      case MAGAZINE_SERVICE_ID:
        erpAdapter.fetch(
          "POST",
          "account/corporate/services/service/unsubscribe",
          {
            product_id: MAGAZINE_SERVICE_ID,
          },
          (response) => {
            setShouldFetch(true);
          }
        );
        break;
      case BM_INPUT_ID:
        erpAdapter.fetch(
          "POST",
          "account/corporate/services/service/unsubscribe",
          {
            product_id,
          },
          (response) => {
            setShouldFetch(true);
          }
        );
        break;
      case BM_OUTPUT_ID:
        unsubscribeFromBmOutputService(erpAdapter, product_id, setShouldFetch);
        break;
      case VALUEAPP_INPUT_ID:
        erpAdapter.fetch(
          "POST",
          "account/valueapp/services/service/unsubscribe",
          {
            product_id,
          },
          (response) => {
            setShouldFetch(true);
          }
        );
        break;
      default:
        break;
    }
  };

  const subscribeToService = useCallback(
    (service_id, product_id) => {
      switch (service_id) {
        case JOBS_SERVICE_ID:
          erpAdapter.fetch(
            "POST",
            "account/corporate/services/service/subscribe",
            {
              product_id,
            },
            (response) => {
              setShouldFetch(true);
              setIsSubscribedToJobs(true);
            }
          );
          break;

        // TODO chanage to the correct endpoint when the API is ready.
        case LAW_SERVICE_ID:
          erpAdapter.fetch(
            "POST",
            "account/corporate/services/service/subscribe",
            {
              product_id,
            },
            (response) => {
              setShouldFetch(true);
            }
          );
          break;

        // TODO chanage to the correct endpoint when the API is ready.
        case MAGAZINE_SERVICE_ID:
          erpAdapter.fetch(
            "POST",
            "account/corporate/services/service/subscribe",
            {
              product_id: MAGAZINE_SERVICE_ID,
            },
            (response) => {
              setShouldFetch(true);
            }
          );
          break;

        // TODO chanage to the correct endpoint when the API is ready.
        case NEWSLETTER_SERVICE_ID:
          erpAdapter.fetch(
            "PUT",
            "account/single/services/newsletter",
            {
              product_ids: product_id,
            },
            (response) => {
              setShouldFetch(true);
            }
          );
          break;
        case BM_INPUT_ID:
          erpAdapter.fetchWithDelay(
            "POST",
            "account/corporate/services/service/subscribe",
            {
              product_id,
              remark: selectedDropdownItems.remark,
            },
            (response) => {
              pgAdapter.setNotificationStatusNew(
                ({ data }) => setShouldFetch(true),
                (result) => null
              );
            },
            null,
            4000
          );
          break;
        case BM_OUTPUT_ID:
          subscribeToBmOutputService(erpAdapter, product_id, selectedBmoutputDropdownItems, noneOptions, setShouldFetch);
          break;
        case VALUEAPP_INPUT_ID:
          erpAdapter.fetchWithDelay(
            "POST",
            "account/valueapp/services/service/subscribe",
            {
              product_id,
              remark: selectedDropdownItem.remark,
            },
            async (response) => {
              const isTokenUpdated = await KeycloakIDPAdapter.getClient().updateToken(-1);
              if (isTokenUpdated) {
                pgAdapter.checkValueAppAuthorization(
                  ({ data }) => setIsSubscribedToValueApp(data),
                  (result) => setIsSubscribedToValueApp(false)
                );
                setShouldFetch(true);
              }
            },
            null,
            4000
          );
          break;
        default:
          break;
      }
    },
    [
      setIsSubscribedToJobs,
      selectedDropdownItem.remark,
      selectedDropdownItems.remark,
      selectedBmoutputDropdownItems,
      setIsSubscribedToValueApp,
      noneOptions,
    ]
  );

  const confirmUnsubscribe = useCallback((currentService, translate) => {
    const modalTitle = translate("account.services.servicebox.confirm.unsubscribe", { service: currentService.name });
    let productCancelationDateMap = currentService?.productCancelationDateMap || [];
    let dynamicProductId = currentService.product_id;
    let dynamicCancelationDate = currentService.cancelDate;
    if (currentService.product_id === "BM_OUTPUT") {
      let [productId, cancelDate] = handleBmOutputProductCancelationDateSelection(productCancelationDateMap);
      dynamicProductId = productId;
      dynamicCancelationDate = cancelDate;
    }

    const cancelDate = new Date(dynamicCancelationDate);

    const modalLead = translate("account.services.servicebox.confirm.unsubscribe.lead", { date: dateFormatter.format(cancelDate) });

    setHandleModalConfirm(() => () => {
      unsubscribeFromService(currentService.service_id, dynamicProductId);
      setModalState((state) => ({ ...state, visible: false }));
    });
    setModalState((state) => ({
      ...state,
      visible: true,
      modalTitle,
      modalLead,
    }));
  }, []);

  const confirmSubscribe = useCallback(
    (currentService, translate) => {
      const modalTitle = translate("account.services.servicebox.confirm.subscribe", { service: currentService.name });
      let prodcutPriceMap = currentService?.prodcutPriceMap || [];
      let modalLead = currentService.lead || "";
      let dynamicProductId = currentService.product_id;
      let dynamicPrice = currentService.price;

      if (currentService.product_id === "BM_OUTPUT") {
        let [productId, price] = handleBmOutputProductSelection(selectedBmoutputDropdownItems, noneOptions, prodcutPriceMap);
        dynamicProductId = productId;
        dynamicPrice = price;
      }

      if (dynamicPrice > 0) {
        modalLead = translate("account.services.servicebox.confirm.subscribe.lead", { price: currencyFormatter.format(dynamicPrice) });
      }

      setHandleModalConfirm(() => () => {
        subscribeToService(currentService.service_id, dynamicProductId);
        setModalState((state) => ({ ...state, visible: false }));
      });

      setModalState((state) => ({
        ...state,
        visible: true,
        modalTitle,
        modalLead,
      }));
    },
    [subscribeToService, selectedBmoutputDropdownItems, noneOptions]
  );

  const confirmBMSave = useCallback(
    (currentService, translate, remark, selectedDropdownItems) => {
      const modalTitle = translate("account.services.servicebox.confirm.update", { service: currentService.name });
      let modalLead = "";
      const added = selectedDropdownItems.remark.filter((value) => !remark.includes(value));
      const removed = remark.filter((value) => !selectedDropdownItems.remark.includes(value));
      if (remark) {
        modalLead = (
          <>
            <p> {translate("account.services.servicebox.confirm.update.lead")} </p>
            {removed.length > 0 && <div> {translate("account.services.servicebox.confirm.update.delete-member", { membership: removed })}</div>}
            {added.length > 0 && <div> {translate("account.services.servicebox.confirm.update.add-member", { membership: added })}</div>}
          </>
        );
      }

      setHandleModalConfirm(() => () => {
        subscribeToService(currentService.service_id, currentService.product_id);
        setModalState((state) => ({ ...state, visible: false }));
      });

      setModalState((state) => ({
        ...state,
        visible: true,
        modalTitle,
        modalLead,
      }));
    },
    [subscribeToService]
  );

  const onMagazineSave = useCallback(() => {
    confirmSubscribe({ service_id: MAGAZINE_SERVICE_ID, name: selectedMagazine, price: 0 }, translate);
  }, [confirmSubscribe, translate, selectedMagazine]);

  const onNewsletterSave = useCallback(
    (updatedCheckboxes) => {
      let subscribedNewsletter = updatedCheckboxes.filter((newsletter) => newsletter.isSubscribed).map((newsletter) => newsletter.name);
      let name = "";
      let product_ids = updatedCheckboxes.filter((newsletter) => newsletter.isSubscribed).map((newsletter) => parseInt(newsletter.id));
      confirmSubscribe({ service_id: NEWSLETTER_SERVICE_ID, product_id: product_ids, name, lead: subscribedNewsletter, price: 0 }, translate);
    },
    [confirmSubscribe, translate]
  );

  const extractPropsFromService = useCallback(
    (service) => {
      if (!service?.service) {
        return {};
      }
      let service_id = service.service[0].service_id;
      let extraProps = {};

      switch (service_id) {
        case REFERENCE_GALLERY_SERVICE_ID:
          extraProps = {
            onManage: () => history.push(pathFactory.create("reference-gallery/manage")),
          };
          break;
        case JOBS_SERVICE_ID:
          {
            let product_id = Object.keys(service.products[0])[0];
            let isCancelled = service.products[0][product_id]["subscription"]["status"] === "cancelled";
            let cancelDate = new Date(service.products[0][product_id]["subscription"]["cancel_date"]);
            let isSubscribed = isCancelled ? true : service.products[0][product_id]["subscription"]["status"] === "subscribed";
            let name = service.service[0].sale_name;
            let price = service.products[0][product_id].details.price;
            product_id = parseInt(product_id);
            const linksMap = {
              de: "de/cms/organisation/mitgliedschaft#firmenmitglied",
              fr: "fr/cms/organisation/affiliation#bureaumembre",
              it: "it/cms/organizzazione/affiliazione#socioditta",
              en: "en/cms/organisation/membership#corporatemembers",
            };
            extraProps = {
              isSubscribed,
              isCancelled,
              onManage: () => history.push(pathFactory.create("jobs/manage")),
              onSubscribe: () => {
                confirmSubscribe({ service_id, product_id, name, price }, translate);
              },
              onUnsubscribe: () => {
                if (isCancelled) {
                  return null;
                }
                return confirmUnsubscribe({ service_id, product_id, name, price, cancelDate }, translate);
              },
              options: {
                url: linksMap[language],
                linkTitle: translate("account.services.servicebox.more-info"),
                price: price,
                pricePhrase: translate("account.services.servicebox.price-phrase"),
                cancelDate,
              },
            };
          }
          break;
        case BM_INPUT_ID:
          {
            setResetBmDropdown(false);
            let product_id = Object.keys(service.products[0])[0];
            let isCancelled = service.products[0][product_id]["subscription"]["status"] === "cancelled";
            let cancelDate = new Date(service.products[0][product_id]["subscription"]["cancel_date"]);
            let isSubscribed = isCancelled ? true : service.products[0][product_id]["subscription"]["status"] === "subscribed";
            let name = service.service[0].sale_name;
            let price = service.products[0][product_id].details.price;
            const dropdownConfigs = service.dropdown.map((dropdown) => {
              const remarks = dropdown.remark || "";
              return {
                name: dropdown.label || "",
                items: dropdown.values.map((item, index) => ({
                  id: `${index + 1}`,
                  label: item.name,
                  active: remarks.includes(item.name),
                })),
                required: true,
              };
            });
            product_id = parseInt(product_id);
            extraProps = {
              isSubscribed,
              isCancelled,
              onManage: () => (window.location.href = process.env.REACT_APP_BENCHMARKING_URL),
              onSubscribe: () => {
                setBmUpdate(false);
                confirmSubscribe({ service_id, product_id, name, price }, translate);
              },
              onUnsubscribe: () => {
                if (isCancelled) {
                  return null;
                }
                return confirmUnsubscribe({ service_id, product_id, name, price, cancelDate }, translate);
              },
              onSave: () => {
                setBmUpdate(false);
                confirmBMSave({ service_id, product_id, name, price }, translate, service.dropdown[0].remark, selectedDropdownItems);
              },
              options: {
                dropdownConfigs: dropdownConfigs,
                onDropdownChange: (name, selectedItems) => {
                  setBmUpdate(true);
                  setSelectedDropdownItems({ remark: selectedItems.map((item) => item.label) });
                  if (isSubscribed && selectedItems.length === 0) {
                    confirmUnsubscribe({ service_id, product_id, name, price, cancelDate }, translate);
                  }
                },
                isSaveButtonActive: bmUpdate,
                price: price,
                pricePhrase: translate("account.services.servicebox.price-phrase"),
                cancelDate,
                resetDropdown: resetBmDropdown,
              },
            };
          }
          break;
        case BM_OUTPUT_ID:
          extraProps = extractBmOutputExtraPropsFromService(
            service,
            setResetBmOutputDropdown,
            setBmOutputUpdate,
            confirmSubscribe,
            confirmUnsubscribe,
            translate,
            noneOptions,
            selectedBmoutputDropdownItems,
            setSelectedBmoutputDropdownItems,
            bmOutputUpdate,
            resetBmOutputDropdown,
            confirmBMSave
          );
          break;
        case VALUEAPP_INPUT_ID:
          {
            const products = service.products;
            let currentProduct = selectedValueAppProduct;
            // if (Object.keys(currentProduct).length === 0) {
            const subscribedProduct = products.find((product) => {
              const key = Object.keys(product)[0];
              return product[key].subscription.status === "subscribed";
            });
            if (subscribedProduct) {
              const key = Object.keys(subscribedProduct)[0];
              currentProduct = {
                id: key,
                details: subscribedProduct[key].details,
                subscription: subscribedProduct[key].subscription,
              };
            } else {
              const firstProduct = products[0];
              const key = Object.keys(firstProduct)[0];
              currentProduct = {
                id: key,
                details: firstProduct[key].details,
                subscription: firstProduct[key].subscription,
              };
            }
            // }

            const subscription = currentProduct.subscription || { status: "unsubscribed", cancel_date: null };
            const product_id = parseInt(currentProduct.id, 10);
            const isCancelled = subscription.status === "cancelled";
            const isSubscribed = isCancelled ? true : subscription.status === "subscribed";
            const name = service.service[0].sale_name;
            const price = currentProduct.details?.price || "";
            const cancelDate = subscription.cancel_date ? new Date(subscription.cancel_date) : null;

            const dropdownConfigs = [
              {
                items: products.map((product) => {
                  const id = Object.keys(product)[0];
                  return {
                    id: id,
                    label: product[id].details.sale_name,
                  };
                }),
                required: true,
                helperText: valueAppHelperText || "",
                ...(isSubscribed && currentProduct.details
                  ? {
                      disabled: true,
                      placeholder: currentProduct.details.sale_name,
                    }
                  : {}),
              },
            ];

            extraProps = {
              isSubscribed,
              isCancelled,
              onManage: () => history.push(pathFactory.create("value-app")),
              onSubscribe: () => {
                confirmSubscribe({ service_id, product_id, name, price }, translate);
              },
              onUnsubscribe: () => {
                if (isCancelled) {
                  return null;
                }
                return confirmUnsubscribe({ service_id, product_id, name, price, cancelDate }, translate);
              },
              options: {
                dropdownConfigs,
                onDropdownChange: (name, selectedItem) => {
                  if (isSubscribed) return;
                  const selectedProd = products.find((product) => Object.keys(product)[0] === selectedItem.id);
                  if (selectedProd) {
                    const id = Object.keys(selectedProd)[0];
                    const details = selectedProd[id].details;
                    setSelectedValueAppProduct({
                      id,
                      details,
                      subscription: selectedProd[id].subscription,
                    });
                    setValueAppHelperText(details.description || "");
                    setValueAppPrice(details.price);
                    setSelectedDropdownItem({ remark: selectedItem.label });
                  }
                },

                price: valueAppPrice,
                pricePhrase: translate("account.services.servicebox.price-phrase"),
                cancelDate,
              },
            };
          }
          break;
        case LAW_SERVICE_ID:
          {
            let product_id = Object.keys(service.products[0])[0];
            const linksMap = {
              de: "de/cms/premium-rechtsauskunft-agb",
              fr: "fr/cms/conseil-juridique-premium-CG",
              it: "it/cms/consulenza-giuridica-premium-CG",
              en: "en/cms/node/174",
            };
            let isCancelled = service.products[0][product_id]["subscription"]["status"] === "cancelled";
            let cancelDate = new Date(service.products[0][product_id]["subscription"]["cancel_date"]);
            let isSubscribed = isCancelled ? true : service.products[0][product_id]["subscription"]["status"] === "subscribed";
            let name = service.service[0].sale_name;
            let price = service.products[0][product_id].details.price;
            product_id = parseInt(product_id);

            extraProps = {
              isSubscribed,
              isCancelled,
              onSubscribe: () => {
                confirmSubscribe({ service_id, product_id, name, price }, translate);
              },
              onUnsubscribe: () => {
                if (isCancelled) {
                  return null;
                }
                return confirmUnsubscribe({ service_id, product_id, name, price, cancelDate }, translate);
              },
              options: {
                url: linksMap[language],
                linkTitle: translate("account.services.servicebox.more-info"),
                price: price,
                pricePhrase: translate("account.services.servicebox.price-phrase"),
                cancelDate,
              },
            };
          }
          break;
        case MAGAZINE_SERVICE_ID:
          extraProps = {
            isSubscribed: isSubscribedToMagazines,
            onSave: onMagazineSave,
            options: {
              name: "Magazines",
              formatBoxes: checkBoxes,
              radioItems: magazines,
              selectedValue: selectedMagazine,
              setSelectedValue: setSelectedMagazine,
              isSaveButtonActive: isMagazineSaveButtonActive,
              setIsSaveButtonActive: setIsMagazineSaveButtonActive,
              onRadioboxChange: onMagazineChange,
            },
          };

          break;
        case NEWSLETTER_SERVICE_ID:
          extraProps = {
            isSubscribed: isSubscribedToNewsLetter,
            onSave: onNewsletterSave,
            options: {
              name: "newsletters",
              checkBoxes: newsletters,
            },
          };
          break;
        default:
          break;
      }

      return extraProps;
    },
    [
      magazines,
      confirmSubscribe,
      confirmUnsubscribe,
      confirmBMSave,
      history,
      pathFactory,
      translate,
      checkBoxes,
      isMagazineSaveButtonActive,
      isSubscribedToMagazines,
      onMagazineChange,
      onMagazineSave,
      selectedMagazine,
      isSubscribedToNewsLetter,
      newsletters,
      onNewsletterSave,
      language,
      valueAppHelperText,
      selectedValueAppProduct,
      valueAppPrice,
      noneOptions,
      bmOutputUpdate,
      resetBmOutputDropdown,
      selectedBmoutputDropdownItems,
    ]
  );
  const updateServicesState = useCallback((service) => {
    if (!service?.service) {
      return {};
    }
    let service_id = service.service[0].service_id;
    switch (service_id) {
      case NEWSLETTER_SERVICE_ID:
        {
          let products = service.products;
          let productNewsletters = products.map((product) => {
            let productValue = Object.values(product)[0];
            let productId = Object.keys(product)[0];
            let productName = productValue.details.sale_name;
            let isSubscribed = productValue.subscription.status === "subscribed";
            let isCancelled = productValue.subscription.status === "cancelled";
            return {
              id: productId,
              name: productName,
              isSubscribed,
              isCancelled,
            };
          });
          const isSubscribedToAnyNewsLetter = productNewsletters.some((e) => e.isSubscribed);
          setIsSubscribedToNewsletter(isSubscribedToAnyNewsLetter);
          setNewsletters(productNewsletters);
        }
        break;
      case MAGAZINE_SERVICE_ID:
        {
          let products = service.products;
          let productMagazines = products.map((product) => {
            let productValue = Object.values(product)[0];
            let productName = productValue.details.sale_name;
            return { value: productName, label: productName };
          });
          let noMagazine = { value: "", label: "Keine" };
          productMagazines.push(noMagazine);
          let uniqueMagazines = productMagazines.reduce((acc, e) => {
            let objectAlreadyExists = acc.find((obj) => e.value === obj.value);
            if (!objectAlreadyExists) {
              acc.push(e);
            }
            return acc;
          }, []);
          setMagazines(uniqueMagazines);

          let selectedProduct = products.find((product) => {
            let productValue = Object.values(product)[0];
            let subscriptionStatus = productValue.subscription.status;
            return subscriptionStatus === "subscribed";
          });
          if (!selectedProduct) {
            setSelectedMagazine("");
            setIsSubscribedToMagazines(false);
          } else {
            let selectedMagazine = Object.values(selectedProduct)[0].details.sale_name;
            setSelectedMagazine(selectedMagazine);
            setIsSubscribedToMagazines(true);
          }
        }
        break;
      default:
        break;
    }
  }, []);

  const extractServicesFromData = useCallback(
    (data) => {
      if (data === null) {
        return [];
      }
      let userRole = user.getRole();
      let filteredServices = null;
      if (userRole === "sia_role_member_corporate") filteredServices = ["2", "3", "4", "5", "6", "8"]; //Valueapp Entrypoint add "7" once we bring back subscription
      if (userRole === "sia_role_corporate") filteredServices = ["2", "6", "8"]; //Valueapp Entrypoint add "7" once we bring back subscription
      if (userRole === "sia_role_member_single") filteredServices = ["2", "8"]; //Valueapp Entrypoint add "7" once we bring back subscription
      if (userRole === "sia_role_single") filteredServices = ["2", "8"]; //Valueapp Entrypoint add "7" once we bring back subscription

      let serviceEntries = Object.entries(data);
      serviceEntries = serviceEntries
        .filter(([key]) => filteredServices.includes(key))
        .sort((a, b) => a - b)
        .map((entry) => entry[1]);
      serviceEntries = serviceEntries.flatMap((entry) => Object.entries(entry));
      let services = serviceEntries.map((entry) => {
        let serviceType = entry[0];
        let service = entry[1];
        let serviceDetails = entry[1].service[0];
        let serviceName = serviceDetails.sale_name;
        let service_id = serviceDetails.service_id;
        let key = Object.keys(service.products[0])[0];
        let serviceDescription =
          service_id === BM_INPUT_ID || service_id === VALUEAPP_INPUT_ID
            ? service.service[0].description
            : service.products[0][key].details.description;
        let extraProps = extractPropsFromService(service);
        let isSubscribed = extraProps.isSubscribed;
        let isCancelled = extraProps.isCancelled;
        return {
          isSubscribed,
          isCancelled,
          service_id,
          type: parseInt(serviceType),
          title: serviceName,
          description: serviceDescription,
          translate: translate,
          ...extraProps,
        };
      });
      if (process.env.REACT_APP_HIDE_BENCHMARKING_SERVICES === "1") {
        let benchmarking_services_ids = [121033, 49890];
        services = services.filter((service) => !benchmarking_services_ids.includes(service.service_id));
      }
      return services;
    },
    [translate, extractPropsFromService, user]
  );

  const updateMagazines = useCallback(
    (data) => {
      let serviceEntries = Object.entries(data);
      serviceEntries = serviceEntries.map((entry) => entry[1]);
      serviceEntries = serviceEntries.flatMap((entry) => Object.entries(entry));
      serviceEntries.forEach((entry) => {
        let serviceType = parseInt(entry[0]);
        let service = entry[1];
        if (serviceType === 4) {
          updateServicesState(service);
        }
      });
    },
    [updateServicesState]
  );
  const [data, setData] = useState(null);

  useEffect(() => {
    let services = extractServicesFromData(data);
    setServices(services);
  }, [data, extractServicesFromData]);

  useEffect(() => {
    if (language && shouldFetch) {
      webPlatformAdapter.getServiceSubscriptions(language, (response) => {
        setShouldFetch(false);
        const { data } = response;
        updateMagazines(data);
        setData(data);
      });
    }
  }, [extractServicesFromData, updateMagazines, language, shouldFetch]);

  if (!isSubscribedToAnyService) {
    return <NotFoundPage />;
  }

  const resetBmServiceBox = () => {
    let serviceEntries = Object.entries(data);
    serviceEntries = serviceEntries.map((entry) => entry[1]);
    serviceEntries = serviceEntries.flatMap((entry) => Object.entries(entry));
    serviceEntries.forEach((entry) => {
      let serviceType = parseInt(entry[0]);
      if (serviceType === 7) {
        setBmUpdate(false);
        setShouldFetch(true);
        setResetBmDropdown(true);
      }
    });
  };

  const handleModalCancel = () => {
    resetBmServiceBox();
    setModalState((state) => ({ ...state, visible: false }));
  };

  const elementToRender = () => {
    if (services.length === 0) {
      return null;
    }
    return services.map((service, i) => <ServiceSubscriptionBox key={`${i}${service.isSubscribed}`} {...service} />);
  };

  return (
    <Page contentNavigation={<UserContentNavigation />} title={"account.name"}>
      <div className="sia-services-page">
        <div className="bx--grid reset-spacings">
          <div className="bx--row">
            <div className="bx--col-lg-2 bx--col-md-0 bx--col-ms-0"></div>
            <div className="bx--col-lg-6 bx--col-md-4 bx--col-sm-4">
              <div className="Services-Container Services-Container-left">
                {services.length > 0 &&
                  elementToRender().map((e, i) => {
                    if (i % 2 === 0) return e;
                    return null;
                  })}
              </div>
            </div>
            <div className="bx--col-lg-6 bx--col-md-4 bx--col-sm-4">
              <div className="Services-Container Services-Container-right">
                {services.length > 0 &&
                  elementToRender().map((e, i) => {
                    if (i % 2 !== 0) return e;
                    return null;
                  })}
              </div>
            </div>
            {modalState.visible ? (
              <Modal
                title={modalState.modalTitle}
                lead={modalState.modalLead}
                onConfirm={handleModalConfirm}
                onCancel={handleModalCancel}
                confirmLabel={translate("modal.button.confirm")}
                cancelLabel={translate("modal.button.cancel")}
              />
            ) : null}
            <div className="bx--col-lg-2 bx--col-md-0 bx--col-ms-0"></div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default ServicesPage;
